import { useMutation, useQueryClient } from "@tanstack/react-query";
import blockAPI from "../../api/BlockAPI";
import { debounceInvalidate } from "../../services/optimistic-updates/debounceInvalidate";
import { calendarQueryKeys } from "../calendar/queries";
import { inboxQueryKeys } from "../inbox/queries";
import { BlockType } from "../../types/block/type";
import { mobaCalendarListState } from "../../recoil/calendar/mobaCalendarListState";
import { useSetRecoilState } from "recoil";
import useFindUpdateInboxQueryKey from "../../hooks/inbox/useFindUpdateInboxQueryKey";
import { useFetchUserCalendarsQuery } from "../userCalendars/queries";
import { UserCalendarAccessType } from "../../types/userCalendars/enum";
import { isAxiosError } from "axios";
import useBlockCalendarSync from "../../hooks/block/useBlockCalendarSync";

const useUpdateBlockCalendar = () => {
  const queryClient = useQueryClient();
  const fetchEditableUserCalendarsQuery = useFetchUserCalendarsQuery({
    calendarAccessType: UserCalendarAccessType.EDITABLE,
  });

  const setMobaCalendarList = useSetRecoilState(mobaCalendarListState);

  const { handleChangeCalendarVisibility } = useBlockCalendarSync();
  const { findUpdateInboxQueryKey } = useFindUpdateInboxQueryKey();

  return useMutation({
    mutationFn: ({
      id,
      payload,
      prevData,
    }: {
      id: number | string;
      payload: { calendarId: number };
      prevData: BlockType;
    }) => {
      // 반복블록인 경우 기준블록 id보냄
      const convertedId = Number(id.toString().split("_")[0]);
      return blockAPI.updateBlockCalendar(convertedId, payload);
    },
    onMutate: async ({ id, payload, prevData }) => {
      if (!fetchEditableUserCalendarsQuery.data) return;

      // 업데이트할 캘린더 찾기
      const updateCalendar = fetchEditableUserCalendarsQuery.data.find(
        (calendar) => calendar.id === payload.calendarId
      );

      // 1. calendar 업데이트
      setMobaCalendarList((prev) =>
        prev.map((block) =>
          block.id === id
            ? { ...block, calendarId: payload.calendarId, calendar: updateCalendar }
            : block
        )
      );

      // 2. inbox 업데이트
      const prevInboxQueryKey = findUpdateInboxQueryKey(prevData);
      if (prevInboxQueryKey) {
        await queryClient.cancelQueries({ queryKey: prevInboxQueryKey });
        queryClient.setQueryData<BlockType[]>(prevInboxQueryKey, (oldData) => {
          if (!oldData) return;
          return oldData.map((block) =>
            block.id === id
              ? { ...block, calendarId: payload.calendarId, calendar: updateCalendar }
              : block
          );
        });
      }

      return { updateCalendar };
    },
    onSuccess: (data, variables, context) => {
      const isRecurringBlock =
        (variables.prevData.recurrence && variables.prevData.recurrence.length > 0) ||
        Boolean(variables.prevData.originalId);

      if (isRecurringBlock) {
        debounceInvalidate.enqueueInvalidate(calendarQueryKeys.all, {
          shouldInvalidateImmediately: true,
        });
        debounceInvalidate.enqueueInvalidate(inboxQueryKeys.all(), {
          shouldInvalidateImmediately: true,
        });
      } else {
        debounceInvalidate.enqueueInvalidate(calendarQueryKeys.all);
        debounceInvalidate.enqueueInvalidate(inboxQueryKeys.all());

        // 변경한 캘린더가 꺼져있는 경우 즉시 invalidate
        const { updateCalendar } = context;
        updateCalendar && handleChangeCalendarVisibility(updateCalendar.id);
      }
    },
    onError: (err) => {
      if (isAxiosError(err)) {
        debounceInvalidate.enqueueInvalidate(calendarQueryKeys.all, {
          shouldInvalidateImmediately: true,
        });
        debounceInvalidate.enqueueInvalidate(inboxQueryKeys.all(), {
          shouldInvalidateImmediately: true,
        });
      }
    },
  });
};

export default useUpdateBlockCalendar;
